import { inject, injectable } from '@patsnap/tahiti-core/shared/inversify';
import Mark from 'mark.js';
import { Emitter } from '@patsnap/tahiti-core';
import { GetClaimCompareMatchRepository } from '../claim-charting-repository';
import { FeatureLocatedModel } from '../../models/feature-located-model';
import { CLAIM_CHARTING_MARK_CLASS_NAME, COMPARE_ORIGINAL_WRAPPER_CLASS_NAME } from '../../constants';
import type { FeatureLocatedState, GetClaimCompareMatchParams } from '../../types';
import type { FeatureLocatedService } from '../feature-located-service';

@injectable()
export class FeatureLocatedServiceImpl implements FeatureLocatedService {
    private _onStatesChangeEmitter = new Emitter<FeatureLocatedState>();
    readonly onStatesChange = this._onStatesChangeEmitter.event;

    constructor(
        @inject(GetClaimCompareMatchRepository)
        private _getClaimFeatureRepository: GetClaimCompareMatchRepository,
        @inject(FeatureLocatedModel)
        private _model: FeatureLocatedModel,
    ) {
        this._model.onStatesChange(this._emitModelDataChange.bind(this));
    }

    private _emitModelDataChange() {
        this._onStatesChangeEmitter.fire(this.getModelData());
    }

    public getModelData() {
        return this._model.modelData;
    }
    
    public updateModelData(data: Partial<FeatureLocatedState>) {
        this._model.update(() => ({
            ...this._model.modelData,
            ...data,
        }));
    }

    public setLocatedPatentInfo({ patentId, text }: { patentId: string; text: string; }) {
        this.updateModelData({
            patentId,
            originText: text,
        });
    }

    public async getFeatureMatchData(params: GetClaimCompareMatchParams): Promise<void> {
        const data = await this._getClaimFeatureRepository.getClaimCompareMatch(params);
        this.updateModelData({
            matchText: data.matchText,
        });
    }


    private _clearNodeGroups(): void {
        this._model.update(() => ({
            ...this._model.modelData,
            nodeGroups: [],
            currentGroup: 1,
        }));
    }

    private _setFeatureMatchNode(wrapper: HTMLElement): void {
        const markNodes = wrapper.querySelectorAll<HTMLElement>(`.${CLAIM_CHARTING_MARK_CLASS_NAME}`);
        let markString = '';
        let matchNodes: HTMLElement[] = [];
        const groups: HTMLElement[] = [];

        markNodes.forEach((node) => {
            markString += node.textContent || '';
            matchNodes.push(node);
            if (markString === this.getModelData().matchText) {
                groups.push(matchNodes[0]);
                matchNodes = [];
                markString = '';
            }
        });
        this._model.update(() => ({
            ...this._model.modelData,
            nodeGroups: groups,
        }));
    }


    public markFeatureInDocument(wrapperClass?: string): void {
        const wrapper = document.querySelector<HTMLElement>(`.${wrapperClass || COMPARE_ORIGINAL_WRAPPER_CLASS_NAME}`);
        if (!wrapper) return;

        this._clearNodeGroups();
        
        const instance = new Mark(wrapper);
        instance.mark(this.getModelData().matchText, {
            className: CLAIM_CHARTING_MARK_CLASS_NAME,
            separateWordSearch: false,
            caseSensitive: true,
            acrossElements: true,
        });

        this._setFeatureMatchNode(wrapper);
        this.scrollToNode();
    }

    public scrollToNode(number: number = this._model.modelData.currentGroup): void {
        const node = this._model.modelData.nodeGroups[number - 1];
        if (!node) return;
        node.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
        });
        this.updateModelData({
            currentGroup: number,
        });
    }
}
